import React, { useState, useEffect } from "react";
import CommonButton from "../../../components/CommonButton";
import { GetAllFaqData } from "../../../types/Response/CustomerResponse";
import {
  ICreateFAQRequest,
  IUpdateAQRequest,
} from "../../../types/Request/CustomerRequest";
import {
  useCreateFAQMutation,
  useLazyGetAllFaqQuery,
  useUpdateFAQMutation,
} from "../../../redux/actions/Admin/footerManagementAdmin";

type ViewMode = "LIST" | "ADD" | "EDIT";

export enum FAQCategory {
  General = 1,
  Services = 2,
  Pets = 3,
  Products = 4,
  Bookings = 5,
  Payments = 6,
  Delivery = 7,
}

export const FAQAdmin: React.FC = () => {
  // ==========================================
  // RTK QUERY HOOKS
  // ==========================================
  const [triggerGetAllFaq, { data: faqApiResponse, isLoading, isError }] =
    useLazyGetAllFaqQuery();
  const [createFAQ, { isLoading: isCreating }] = useCreateFAQMutation();
  const [updateFAQ, { isLoading: isUpdating }] = useUpdateFAQMutation();

  useEffect(() => {
    triggerGetAllFaq();
  }, [triggerGetAllFaq]);

  // Fallback array extract from the API response envelope structure
  const faqs: GetAllFaqData[] =
    faqApiResponse?.value?.data?.map((item) => item) || [];

  // Navigation & Data Context States
  const [currentView, setCurrentView] = useState<ViewMode>("LIST");
  const [selectedFaq, setSelectedFaq] = useState<GetAllFaqData | null>(null);

  // Form Fields State
  const [questionInput, setQuestionInput] = useState("");
  const [answerInput, setAnswerInput] = useState("");
  const [categoryInput, setCategoryInput] = useState<FAQCategory>(
    FAQCategory.General
  );
  const [isActiveInput, setIsActiveInput] = useState(true);
  const [sortOrderInput, setSortOrderInput] = useState<number>(1);

  // Map Category IDs to display names for human readability
  const getCategoryName = (catId: number): string => {
    switch (catId) {
      case 1:
        return "General";
      case 2:
        return "Account";
      case 3:
        return "Billing";
      case 4:
        return "Technical";
      default:
        return "General";
    }
  };

  // ==========================================
  // NAVIGATION HANDLERS
  // ==========================================
  const openAddPage = () => {
    setSelectedFaq(null);
    setQuestionInput("");
    setAnswerInput("");
    setCategoryInput(1);
    setIsActiveInput(true);
    setSortOrderInput(faqs.length + 1);
    setCurrentView("ADD");
  };

  const openEditPage = (faq: GetAllFaqData) => {
    setSelectedFaq(faq);
    setQuestionInput(faq.question);
    setAnswerInput(faq.answer);
    setCategoryInput(faq.category);
    setIsActiveInput(faq.isActive);
    setSortOrderInput(faq.sortOrder);
    setCurrentView("EDIT");
  };

  const navigateBack = () => {
    setCurrentView("LIST");
    setSelectedFaq(null);
  };

  // ==========================================
  // MUTATION ACCELERATORS / API HANDLERS
  // ==========================================
  const handleToggleStatus = async (faq: GetAllFaqData) => {
    try {
      const updatePayload: IUpdateAQRequest = {
        id: faq.id,
        category: faq.category,
        question: faq.question,
        answer: faq.answer,
        sortOrder: faq.sortOrder,
        isActive: !faq.isActive,
      };
      await updateFAQ(updatePayload).unwrap();
      triggerGetAllFaq();
    } catch (error) {
      console.error("Failed to toggle FAQ active status:", error);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (currentView === "ADD") {
        const newFaqPayload: ICreateFAQRequest = {
          category: categoryInput,
          question: questionInput,
          answer: answerInput,
          sortOrder: Number(sortOrderInput),
          isActive: isActiveInput,
        };
        await createFAQ(newFaqPayload).unwrap();
        triggerGetAllFaq();
      } else if (currentView === "EDIT" && selectedFaq) {
        const updateFaqPayload: IUpdateAQRequest = {
          id: selectedFaq.id,
          category: categoryInput,
          question: questionInput,
          answer: answerInput,
          sortOrder: Number(sortOrderInput),
          isActive: isActiveInput,
        };
        await updateFAQ(updateFaqPayload).unwrap();
        triggerGetAllFaq();
      }
      navigateBack();
    } catch (error) {
      console.error("Failed to commit FAQ transaction payload:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500 font-medium">
        Loading production FAQs from system administration portal...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-8 text-center text-red-500 font-medium">
        Error connecting to administrative services. Verify authentication or
        API gateways.
      </div>
    );
  }

  return (
    <div className="p-4 min-h-screen text-gray-900 w-full max-w-full overflow-x-hidden box-border">
      {/* 1. MAIN LIST VIEW */}
      {currentView === "LIST" && (
        <div className="max-w-7xl mx-auto w-full">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-7">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                FAQ Management Panel
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Monitor, create, update, or instantly toggle your backend FAQ
                assets.
              </p>
            </div>
            <CommonButton
              onClick={openAddPage}
              text="+ Add New FAQ Point"
              variant="filled"
              rounded="md"
              textContainerClassName="px-2 py-2"
            />
          </div>

          {/* FIXED Desktop View Table Container */}
          <div className="hidden lg:block rounded-lg border border-gray-200 w-full overflow-hidden bg-white">
            <table className="w-full text-left border-collapse table-auto">
              <thead>
                <tr className="border-b border-gray-200 text-gray-700 text-sm font-semibold bg-gray-50/50">
                  <th className="p-4 w-[6%] text-center">Order</th>
                  <th className="p-4 w-[14%]">Category</th>
                  <th className="p-4 w-[30%]">Question</th>
                  <th className="p-4 w-[38%]">Answer</th>
                  <th className="p-4 w-[12%] text-center">Status</th>
                  <th className="p-4 w-[10%] text-right pr-6">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-sm">
                {faqs.map((faq) => (
                  <tr key={faq.id} className="hover:bg-gray-50/50 align-top">
                    {/* Sort Order Column */}
                    <td className="p-4 font-mono text-center text-gray-500">
                      {faq.sortOrder}
                    </td>

                    {/* Category Column */}
                    <td className="p-4">
                      <span className="inline-block bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-xs font-medium border border-indigo-100 whitespace-nowrap">
                        {faq.categoryName || getCategoryName(faq.category)}
                      </span>
                    </td>

                    {/* Question Column */}
                    <td className="p-4 font-semibold text-gray-900 break-words whitespace-normal leading-relaxed">
                      {faq.question}
                    </td>

                    {/* Answer Column */}
                    <td className="p-4 text-gray-600 break-words whitespace-normal leading-relaxed">
                      {faq.answer}
                    </td>

                    {/* Switch Toggle Control */}
                    <td className="p-4 text-center">
                      <div className="inline-flex flex-col items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(faq)}
                          className={`w-12 h-6 rounded-full relative transition-colors duration-200 focus:outline-none shadow-inner ${
                            faq.isActive ? "bg-green-600" : "bg-gray-300"
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded-full bg-white absolute top-0.5 shadow transition-all duration-200 ${
                              faq.isActive ? "left-[25px]" : "left-0.5"
                            }`}
                          />
                        </button>
                        <span
                          className={`text-[10px] font-bold tracking-wider ${
                            faq.isActive ? "text-green-600" : "text-gray-500"
                          }`}
                        >
                          {faq.isActive ? "ACTIVE" : "DISABLED"}
                        </span>
                      </div>
                    </td>

                    {/* Edit Trigger */}
                    <td className="p-4 text-right pr-6">
                      <CommonButton
                        onClick={() => openEditPage(faq)}
                        text="Edit"
                        variant="outline"
                        rounded="md"
                        textContainerClassName="px-4 py-1.5"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile & Tablet Card Layout */}
          <div className="block lg:hidden space-y-4 w-full">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm space-y-4 w-full box-border"
              >
                <div className="flex justify-between items-center border-b border-gray-100 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 font-mono">
                      #{faq.sortOrder}
                    </span>
                    <span className="inline-block bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded text-xs font-semibold border border-indigo-100">
                      {faq.categoryName || getCategoryName(faq.category)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggleStatus(faq)}
                      className={`w-10 h-5 rounded-full relative transition-colors duration-200 focus:outline-none shadow-inner ${
                        faq.isActive ? "bg-green-600" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full bg-white absolute top-0.5 shadow transition-all duration-200 ${
                          faq.isActive ? "left-[21px]" : "left-0.5"
                        }`}
                      />
                    </button>
                    <span
                      className={`text-[10px] font-bold tracking-wider ${
                        faq.isActive ? "text-green-600" : "text-gray-500"
                      }`}
                    >
                      {faq.isActive ? "ACTIVE" : "DISABLED"}
                    </span>
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-[11px] font-bold tracking-wide uppercase text-gray-400 block">
                    Question
                  </span>
                  <p className="font-semibold text-gray-900 text-base leading-snug break-words">
                    {faq.question}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-[11px] font-bold tracking-wide uppercase text-gray-400 block">
                    Answer
                  </span>
                  <p className="text-gray-600 text-sm leading-relaxed break-words">
                    {faq.answer}
                  </p>
                </div>

                <div className="pt-2 border-t border-gray-100 flex justify-end">
                  <CommonButton
                    onClick={() => openEditPage(faq)}
                    text="Edit Content"
                    variant="outline"
                    rounded="md"
                    textContainerClassName="px-2 py-2"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2. DEDICATED FORM PAGE (ADD or EDIT Modes) */}
      {(currentView === "ADD" || currentView === "EDIT") && (
        <div className="max-w-2xl mx-auto bg-white p-5 sm:p-8 rounded-lg shadow-sm border border-gray-200 w-full box-border">
          <div className="mb-4">
            <CommonButton
              onClick={navigateBack}
              text="← Back to List"
              variant="outline"
              rounded="md"
              textContainerClassName="px-2 py-2"
            />
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-5">
            {currentView === "ADD"
              ? "Create New FAQ Field"
              : "Modify Existing FAQ Field"}
          </h2>

          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1.5 font-semibold text-sm text-gray-700">
                  Target Group / Category
                </label>
                <select
                  value={categoryInput}
                  onChange={(e) =>
                    setCategoryInput(Number(e.target.value) as FAQCategory)
                  }
                  className="w-full p-2.5 rounded border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
                >
                  <option value={FAQCategory.General}>General</option>
                  <option value={FAQCategory.Services}>Services</option>
                  <option value={FAQCategory.Pets}>Pets</option>
                  <option value={FAQCategory.Products}>Products</option>
                  <option value={FAQCategory.Bookings}>Bookings</option>
                  <option value={FAQCategory.Payments}>Payments</option>
                  <option value={FAQCategory.Delivery}>Delivery</option>
                </select>
              </div>

              <div>
                <label className="block mb-1.5 font-semibold text-sm text-gray-700">
                  Display Position Order
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  value={sortOrderInput}
                  onChange={(e) => setSortOrderInput(Number(e.target.value))}
                  className="w-full p-2.5 rounded border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none box-border"
                />
              </div>
            </div>

            <div>
              <label className="block mb-1.5 font-semibold text-sm text-gray-700">
                Question Prompt
              </label>
              <input
                type="text"
                required
                value={questionInput}
                onChange={(e) => setQuestionInput(e.target.value)}
                placeholder="Ex: What happens when my subscription period finishes?"
                className="w-full p-2.5 rounded border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none box-border"
              />
            </div>

            <div>
              <label className="block mb-1.5 font-semibold text-sm text-gray-700">
                Public Resolution Text / Answer
              </label>
              <textarea
                required
                rows={6}
                value={answerInput}
                onChange={(e) => setAnswerInput(e.target.value)}
                placeholder="Provide a descriptive answer without abbreviation constraints..."
                className="w-full p-2.5 rounded border border-gray-300 text-sm leading-relaxed focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none box-border resize-y"
              />
            </div>

            <div className="flex items-center gap-3 py-2">
              <input
                type="checkbox"
                id="isActiveInput"
                checked={isActiveInput}
                onChange={(e) => setIsActiveInput(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="isActiveInput"
                className="text-sm font-semibold text-gray-700 select-none"
              >
                Set live visibly to clients immediately upon publishing
              </label>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 border-t border-gray-200 pt-5">
              <CommonButton
                onClick={navigateBack}
                text="Cancel"
                variant="outline"
                rounded="md"
                textContainerClassName="px-2 py-2"
              />
              <CommonButton
                disabled={isCreating || isUpdating}
                text={
                  isCreating || isUpdating
                    ? "Saving changes..."
                    : currentView === "ADD"
                    ? "Publish Item"
                    : "Update Content Changes"
                }
                variant="filled"
                rounded="md"
                textContainerClassName="px-2 py-2"
              />
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
